/**
 * HUB-E — Onglet Stock du Centre Activité.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import useActiviteStock from '../../../hooks/activite/useActiviteStock';
import ActiviteStockAccessBanner from '../ActiviteStockAccessBanner';
import ActiviteStockKpiStrip from '../ActiviteStockKpiStrip';
import ActiviteStockSectionTabs from '../ActiviteStockSectionTabs';
import ActiviteStockMovementFilters from '../ActiviteStockMovementFilters';
import ActiviteStockMovementCard from '../ActiviteStockMovementCard';
import ActiviteStockAlertCard from '../ActiviteStockAlertCard';
import ActiviteStockTopList from '../ActiviteStockTopList';
import { ACTIVITE_TAB_IDS } from '../../../utils/activite/activityTabs';
import { STOCK_SECTIONS } from '../../../utils/stock/stockDisplay';

export default function ActiviteStockTab({ filters }) {
  const { api } = useAuth();
  const [activeSection, setActiveSection] = useState(STOCK_SECTIONS.OVERVIEW);

  const {
    movements,
    recentMovements,
    transfers,
    ruptures,
    faible,
    magasinCounts,
    topSold,
    topActive,
    kpis,
    loading,
    error,
    movementTypeFilter,
    setMovementTypeFilter,
  } = useActiviteStock(api, filters);

  const sectionCounts = useMemo(() => ({
    [STOCK_SECTIONS.OVERVIEW]: recentMovements.length,
    [STOCK_SECTIONS.MOUVEMENTS]: movements.length,
    [STOCK_SECTIONS.ALERTES]: ruptures.length + faible.length,
    [STOCK_SECTIONS.TRANSFERTS]: transfers.length,
  }), [recentMovements, movements, ruptures, faible, transfers]);

  const renderOverview = () => (
    <div className="space-y-4">
      {(ruptures.length > 0 || faible.length > 0) && (
        <div className="space-y-2" data-testid="activite-stock-overview-alerts">
          <h3 className="text-sm font-semibold text-[var(--rm-white)]">Alertes prioritaires</h3>
          {ruptures.slice(0, 5).map((a) => (
            <ActiviteStockAlertCard key={`r-${a.reference}-${a.magasin_id}`} article={a} variant="rupture" />
          ))}
          {faible.slice(0, 3).map((a) => (
            <ActiviteStockAlertCard key={`f-${a.reference}-${a.magasin_id}`} article={a} variant="faible" />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ActiviteStockTopList
          title="Articles les plus vendus (période)"
          items={topSold}
          valueKey="quantite"
          valueLabel="vendus"
        />
        <ActiviteStockTopList
          title="Activité récente (mouvements)"
          items={topActive}
          valueKey="count"
          valueLabel="mvt."
        />
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-[var(--rm-white)]">20 derniers mouvements</h3>
          <Link
            to="/stock/mouvements"
            className="text-xs text-[var(--rm-gray-400)] hover:text-[var(--rm-white)] inline-flex items-center gap-1 min-h-[44px] px-2"
            data-testid="activite-stock-link-all-movements"
          >
            Tous les mouvements
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><div className="loading-spinner" /></div>
        ) : recentMovements.length === 0 ? (
          <p className="text-sm text-[var(--rm-gray-500)] text-center py-8" data-testid="activite-stock-recent-empty">Aucun mouvement</p>
        ) : (
          <div className="space-y-3">
            {recentMovements.map((m) => (
              <ActiviteStockMovementCard key={m.id} movement={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderMouvements = () => (
    <div className="space-y-4">
      <ActiviteStockMovementFilters value={movementTypeFilter} onChange={setMovementTypeFilter} />
      {loading ? (
        <div className="flex justify-center py-12"><div className="loading-spinner" /></div>
      ) : movements.length === 0 ? (
        <p className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-stock-movements-empty">Aucun mouvement</p>
      ) : (
        <div className="space-y-3" data-testid="activite-stock-movements-list">
          {movements.slice(0, 30).map((m) => (
            <ActiviteStockMovementCard key={m.id} movement={m} />
          ))}
        </div>
      )}
    </div>
  );

  const renderAlertes = () => (
    <div className="space-y-4">
      {ruptures.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[var(--rm-red-light)]">Ruptures ({ruptures.length})</h3>
          {ruptures.map((a) => (
            <ActiviteStockAlertCard key={`rupture-${a.reference}-${a.magasin_id}`} article={a} variant="rupture" />
          ))}
        </div>
      )}
      {faible.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-[var(--rm-gray-200)]">Stock faible ({faible.length})</h3>
          {faible.map((a) => (
            <ActiviteStockAlertCard key={`faible-${a.reference}-${a.magasin_id}`} article={a} variant="faible" />
          ))}
        </div>
      )}
      {!ruptures.length && !faible.length && (
        <p className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-stock-alerts-empty">Aucune alerte</p>
      )}
    </div>
  );

  const renderTransferts = () => (
    <div className="space-y-4">
      <p className="text-xs text-[var(--rm-gray-500)]" data-testid="activite-stock-transferts-hint">
        Consultation uniquement. Transferts inter-magasin M1 ↔ M2 : réservés au super administrateur (traçabilité complète sur /stock/mouvements).
      </p>
      {transfers.length === 0 ? (
        <p className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-stock-transferts-empty">Aucun transfert sur la période</p>
      ) : (
        <div className="space-y-3" data-testid="activite-stock-transferts-list">
          {transfers.map((m) => (
            <ActiviteStockMovementCard key={m.id} movement={m} />
          ))}
        </div>
      )}
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case STOCK_SECTIONS.MOUVEMENTS: return renderMouvements();
      case STOCK_SECTIONS.ALERTES: return renderAlertes();
      case STOCK_SECTIONS.TRANSFERTS: return renderTransferts();
      default: return renderOverview();
    }
  };

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 space-y-4"
      style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
      data-testid="activite-stock-tab"
    >
      <ActiviteStockAccessBanner />

      <ActiviteStockKpiStrip
        kpis={kpis}
        magasinId={filters.magasin_id}
        magasinCounts={magasinCounts}
      />

      <ActiviteStockSectionTabs
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        counts={sectionCounts}
      />

      {error && (
        <div
          className="rounded-xl border p-4 text-sm text-[var(--rm-red-light)]"
          style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)' }}
          data-testid="activite-stock-error"
        >
          {error}
        </div>
      )}

      {renderSection()}

      <span className="sr-only" data-testid="activite-stock-tab-id">{ACTIVITE_TAB_IDS.STOCK}</span>
    </div>
  );
}
